import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Link, LinkInsert, EntityType } from '@/types/link';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type LinkRow = Database['public']['Tables']['links']['Row'];

const mapLinkRow = (row: LinkRow): Link => ({
  id: row.id,
  source_type: row.source_type as EntityType,
  source_id: row.source_id,
  target_type: row.target_type as EntityType,
  target_id: row.target_id,
  org_id: row.org_id,
  created_at: row.created_at,
});

interface LinkState {
  links: Link[];
  loading: boolean;
  creating: boolean;

  loadLinks: (sourceType?: EntityType, sourceId?: string) => Promise<void>;
  createLink: (link: LinkInsert) => Promise<Link | null>;
  deleteLink: (id: string) => Promise<void>;
  bulkCreateLinks: (sourceType: EntityType, sourceId: string, targetType: EntityType, targetIds: string[]) => Promise<void>;
  getLinkedEntities: (entityType: EntityType, entityId: string) => Link[];
}

export const useLinkStore = create<LinkState>()((set, get) => ({
  links: [],
  loading: false,
  creating: false,

  loadLinks: async (sourceType?: EntityType, sourceId?: string) => {
    // Only show loading if we have no links yet (prevents flickering on subsequent loads)
    const currentLinks = get().links;
    if (currentLinks.length === 0) {
      set({ loading: true });
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        set({ loading: false });
        return;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile?.org_id) {
        set({ loading: false });
        return;
      }

      let query = supabase
        .from('links')
        .select('*')
        .eq('org_id', profile.org_id);

      if (sourceType && sourceId) {
        query = query.or(`source_type.eq.${sourceType},target_type.eq.${sourceType}`)
          .or(`source_id.eq.${sourceId},target_id.eq.${sourceId}`);
      }

      const { data, error } = await query;

      if (error) throw error;
      const normalizedLinks = (data ?? []).map(mapLinkRow);
      set({ links: normalizedLinks, loading: false });
    } catch (error) {
      console.error('Error loading links:', error);
      toast.error('Failed to load relationships');
      set({ loading: false });
    }
  },

  createLink: async (link) => {
    // Prevent duplicate rapid clicks / race conditions
    if (get().creating) return null;
    set({ creating: true });
    
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      // Check if link already exists
      const existingLink = get().links.find(
        l => (l.source_type === link.source_type && l.source_id === link.source_id &&
              l.target_type === link.target_type && l.target_id === link.target_id) ||
             (l.source_type === link.target_type && l.source_id === link.target_id &&
              l.target_type === link.source_type && l.target_id === link.source_id)
      );

      if (existingLink) {
        toast.info('Relationship already exists');
        return existingLink;
      }

      const { data, error } = await supabase
        .from('links')
        .insert({ ...link, org_id: profile.org_id })
        .select()
        .single();

      if (error) throw error;

      const createdLink = mapLinkRow(data as LinkRow);

      set(state => ({ links: [...state.links, createdLink] }));
      toast.success('Relationship created');
      return createdLink;
    } catch (error) {
      console.error('Error creating link:', error);
      toast.error('Failed to create relationship');
      return null;
    } finally {
      set({ creating: false });
    }
  },

  deleteLink: async (id) => {
    try {
      const { error } = await supabase
        .from('links')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        links: state.links.filter(l => l.id !== id)
      }));
      toast.success('Relationship deleted');
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error('Failed to delete relationship');
    }
  },

  bulkCreateLinks: async (sourceType, sourceId, targetType, targetIds) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return;
      }

      // Filter out targetIds that already have links (in either direction)
      const existingLinks = get().links;
      const newTargetIds = targetIds.filter(targetId => {
        const exists = existingLinks.some(l =>
          (l.source_type === sourceType && l.source_id === sourceId &&
           l.target_type === targetType && l.target_id === targetId) ||
          (l.source_type === targetType && l.source_id === targetId &&
           l.target_type === sourceType && l.target_id === sourceId)
        );
        return !exists;
      });

      const skippedCount = targetIds.length - newTargetIds.length;

      if (newTargetIds.length === 0) {
        if (skippedCount > 0) {
          toast.info(`${skippedCount} relationship(s) already exist`);
        }
        return;
      }

      const linksToCreate: Database['public']['Tables']['links']['Insert'][] = newTargetIds.map(targetId => ({
        source_type: sourceType,
        source_id: sourceId,
        target_type: targetType,
        target_id: targetId,
        org_id: profile.org_id!
      }));

      const { data, error } = await supabase
        .from('links')
        .insert(linksToCreate)
        .select();

      if (error) throw error;

      const createdLinks = (data ?? []).map(mapLinkRow);
      set(state => ({ links: [...state.links, ...createdLinks] }));
      
      if (skippedCount > 0) {
        toast.success(`${newTargetIds.length} created, ${skippedCount} already existed`);
      } else {
        toast.success(`${newTargetIds.length} relationship(s) created`);
      }
    } catch (error) {
      console.error('Error creating bulk links:', error);
      toast.error('Failed to create relationships');
    }
  },

  getLinkedEntities: (entityType, entityId) => {
    return get().links.filter(l =>
      (l.source_type === entityType && l.source_id === entityId) ||
      (l.target_type === entityType && l.target_id === entityId)
    );
  },
}));
